import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
  type OnChanges,
  type OnDestroy,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, type FormGroup } from '@angular/forms';
import { LetDirective } from '@ngrx/component';
import { PorscheDesignSystemModule, ToastManager } from '@porsche-design-system/components-angular';
import { BehaviorSubject, distinctUntilChanged, tap } from 'rxjs';
import { SubSink } from 'subsink';
import type {
  EditInventoryStatus,
  InventoryStatus,
} from '../../../shared/models/api/inventory-status.model';
import type { ControlsOf } from '../../../shared/models/controls-of.model';
import type { NgChanges } from '../../../shared/models/ng-changes.model';
import { InventoryStatusService } from '../../../shared/services/api/inventory-status.service';
import { FormIdService } from '../../../shared/services/form-id.service';
import { OpaValidators } from '../../../shared/validators/opa.validators';
import type { EditInventoryStatusFormValue } from './inventory-status-edit.models';

@Component({
  selector: 'opa-inventory-status-edit',
  imports: [CommonModule, PorscheDesignSystemModule, ReactiveFormsModule, LetDirective],
  templateUrl: './inventory-status-edit.component.html',
  styleUrl: './inventory-status-edit.component.scss',
})
export class InventoryStatusEditComponent implements OnChanges, OnDestroy {
  private readonly formBuilder = inject(FormBuilder).nonNullable;
  private readonly inventoryStatusService = inject(InventoryStatusService);
  private readonly toastManager = inject(ToastManager);
  readonly formId = inject(FormIdService).getId();

  @Input({ required: true }) open = false;
  @Input({ required: true }) inventoryStatus: InventoryStatus | undefined = undefined;
  @Output() dismiss = new EventEmitter<boolean | undefined>();

  private readonly subSink = new SubSink();
  readonly form = this.buildForm();
  private readonly submitting = new BehaviorSubject(false);
  readonly submitting$ = this.submitting.pipe(distinctUntilChanged());

  ngOnChanges(changes: NgChanges<InventoryStatusEditComponent>): void {
    if (changes.open && this.open) this.resetForm(this.inventoryStatus);
  }

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  close(success?: boolean): void {
    this.dismiss.emit(success);
  }

  submit(): void {
    if (!this.form.valid || !this.inventoryStatus) {
      this.form.markAllAsTouched();
      return;
    }

    this.submitting.next(true);
    const formValue = this.form.getRawValue();
    const status = this.formValueToStatus(formValue);
    this.subSink.sink = this.inventoryStatusService
      .editInventoryStatus(this.inventoryStatus.id, status)
      .pipe(
        tap({
          next: (editedStatus) => {
            this.toastManager.addMessage({
              text: `Status '${editedStatus.description}' edited`,
              state: 'success',
            });
            this.submitting.next(false);
            this.close(true);
          },
          error: () => this.submitting.next(false),
        }),
      )
      .subscribe();
  }

  private buildForm(): FormGroup<ControlsOf<EditInventoryStatusFormValue>> {
    const form = this.formBuilder.group<ControlsOf<EditInventoryStatusFormValue>>({
      description: this.formBuilder.control(null, [OpaValidators.required()]),
    });

    return form;
  }

  private formValueToStatus(form: EditInventoryStatusFormValue): EditInventoryStatus {
    const inventoryStatus: EditInventoryStatus = {
      description: form.description!,
      name: this.inventoryStatus!.name,
    };
    return inventoryStatus;
  }

  private resetForm(inventoryStatus?: InventoryStatus): void {
    this.form.reset();
    if (!inventoryStatus) return;

    this.form.patchValue({
      description: inventoryStatus.description,
    });
  }
}
